/* <eg-search-bar> — snippets/eg-search-bar.liquid.
   Fetches sections/eg-predictive-search.liquid as the user types and
   shows it in the panel. Arrow keys move through the results, Enter
   opens the selected one (or submits the search), Escape closes.
   debounce() comes from global.js (loaded on every page). */
if (!customElements.get('eg-search-bar')) {
  class EgSearchBar extends HTMLElement {
    #controller = null;
    #cache = new Map();
    #options = [];
    #active = -1;

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-eg-search-input]');
      this.panel = this.querySelector('[data-eg-search-panel]');
      this.status = this.querySelector('[data-eg-search-status]');
      this.clearButton = this.querySelector('[data-eg-search-clear]');
      if (!this.form || !this.input || !this.panel || !this.dataset.url) return;

      this.onInput = this.#onInput.bind(this);
      this.onKeydown = this.#onKeydown.bind(this);
      this.onFocus = this.#onFocus.bind(this);
      this.onFocusOut = this.#onFocusOut.bind(this);
      this.onPointerDown = this.#onPointerDown.bind(this);
      this.onPanelClick = this.#onPanelClick.bind(this);
      this.onPageShow = this.#onPageShow.bind(this);
      this.onClear = this.#onClear.bind(this);
      /* Reads the term when it fires, so a search queued before the field
         was cleared finds it empty and does nothing. */
      this.searchSoon = debounce(() => this.#search(this.#term), 220);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('focus', this.onFocus);
      this.addEventListener('focusout', this.onFocusOut);
      this.panel.addEventListener('click', this.onPanelClick);
      this.clearButton?.addEventListener('click', this.onClear);
      document.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      this.#controller?.abort();
      document.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    get #term() {
      return this.input.value.trim();
    }

    #onInput() {
      this.#syncClear();
      if (!this.#term) {
        this.#controller?.abort();
        this.close();
        return;
      }
      this.searchSoon();
    }

    #onFocus() {
      if (this.#term && this.panel.childElementCount) this.open();
    }

    #onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.close();
    }

    #onPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    /* Back/forward restores the typed term from the page cache; start empty. */
    #onPageShow(event) {
      if (!event.persisted) return;
      this.input.value = '';
      this.#syncClear();
      this.close();
    }

    /* X: empty the field, drop the results, keep the cursor in the field. */
    #onClear() {
      this.#controller?.abort();
      this.input.value = '';
      this.#syncClear();
      this.close();
      this.input.focus();
    }

    #syncClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    #onPanelClick(event) {
      if (!event.target.closest('[data-eg-search-all]')) return;
      this.form.requestSubmit();
    }

    #onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (this.panel.hidden || !this.#options.length) return;
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          const count = this.#options.length;
          this.#select((this.#active + step + count) % count);
          return;
        }
        case 'Enter': {
          if (this.panel.hidden || this.#active < 0) return;
          event.preventDefault();
          this.#options[this.#active].click();
          return;
        }
        case 'Escape': {
          if (this.panel.hidden) return;
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      }
    }

    async #search(term) {
      if (!term) return;
      const key = term.toLowerCase();
      if (this.#cache.has(key)) {
        this.#render(this.#cache.get(key));
        return;
      }

      this.#controller?.abort();
      this.#controller = new AbortController();

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('resources[type]', 'product,collection,article,page');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: this.#controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser()
          .parseFromString(await response.text(), 'text/html')
          .querySelector('[data-eg-search-results]');
        if (!html) return;
        this.#cache.set(key, html.outerHTML);
        if (this.#term.toLowerCase() === key) this.#render(html.outerHTML);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('eg-search-bar:', error);
          this.close();
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    #render(html) {
      this.panel.innerHTML = html;
      this.#options = [...this.panel.querySelectorAll('[data-eg-search-option]')];
      this.#options.forEach((option, index) => {
        option.id = `${this.panel.id}-option-${index}`;
      });
      this.#active = -1;
      this.input.removeAttribute('aria-activedescendant');

      const count = this.panel.querySelector('[data-eg-search-count]');
      if (this.status && count) {
        this.status.textContent = '';
        requestAnimationFrame(() => {
          this.status.textContent = count.textContent.trim();
        });
      }

      if (document.activeElement === this.input) this.open();
    }

    #select(index) {
      this.#options[this.#active]?.setAttribute('aria-selected', 'false');
      this.#active = index;
      const option = this.#options[index];
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.#options[this.#active]?.setAttribute('aria-selected', 'false');
      this.#active = -1;
    }
  }

  customElements.define('eg-search-bar', EgSearchBar);
}
